import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  FormSection,
  PageShell,
  SectionCard,
  Skeleton,
  StatusBadge,
} from '@/components/ui';
import { useMyAccountQuery } from '../hooks/useMyAccountQuery';
import { AdminRoleChips } from '../components/AdminRoleChips';
import { TwoFactorRegenerateDialog } from '../components/TwoFactorRegenerateDialog';
import { PasswordChangeForm } from '../components/PasswordChangeForm';
import { PhoneTwoFactorEnroll } from '../components/PhoneTwoFactorEnroll';
import type { AdminRole } from '../types/admin';

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ko-KR');
  } catch {
    return iso;
  }
};

export const MyAccountPage = () => {
  const { t } = useTranslation('common');
  const query = useMyAccountQuery();
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  if (query.isPending) {
    return (
      <PageShell title={t('admin.me.title')} data-testid="admin-my-account-page">
        <div className="space-y-3" role="status" aria-live="polite">
          <Skeleton className="h-[44px] w-full" />
          <Skeleton className="h-[160px] w-full" />
        </div>
      </PageShell>
    );
  }

  if (query.isError || !query.data) {
    return (
      <PageShell title={t('admin.me.title')} data-testid="admin-my-account-page">
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('admin.me.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('admin.common.retry')}
          </Button>
        </div>
      </PageShell>
    );
  }

  const me = query.data;
  // be-user H-2: me 응답에 twoFactorEnabled+twoFactorMethod 추가 예정(요청함). 추가 시 자동 반영.
  // 휴대폰 2FA 등록 = 2FA 활성 + method=PHONE.
  const meExt = me as { twoFactorEnabled?: boolean; twoFactorMethod?: string | null };
  const phoneEnrolled = meExt.twoFactorEnabled === true && meExt.twoFactorMethod === 'PHONE';

  return (
    <PageShell title={t('admin.me.title')} data-testid="admin-my-account-page">
      <SectionCard title={t('admin.me.sectionProfile')}>
        <div className="flex flex-col gap-3">
          <FormSection label={t('admin.accounts.form.fields.name')} density="compact">
            <span className="text-sm" style={{ color: 'var(--content-strong)' }}>
              {me.name}
            </span>
          </FormSection>
          <FormSection label={t('admin.accounts.form.fields.loginId')} density="compact">
            <span className="text-sm" style={{ color: 'var(--content-default)' }}>
              {me.loginId}
            </span>
          </FormSection>
          <FormSection label={t('admin.accounts.form.fields.email')} density="compact">
            <span className="text-sm" style={{ color: 'var(--content-default)' }}>
              {me.email}
            </span>
          </FormSection>
          <FormSection label={t('admin.accounts.cols.roles')} density="compact">
            <AdminRoleChips roles={me.roles as ReadonlyArray<AdminRole>} />
          </FormSection>
          <FormSection label={t('admin.accounts.cols.lastLoginAt')} density="compact">
            <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
              {formatDate(me.lastLoginAt)}
            </span>
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard title={t('admin.me.sectionPassword')}>
        <PasswordChangeForm />
      </SectionCard>

      <SectionCard title={t('admin.me.sectionPhoneTwoFactor')}>
        <PhoneTwoFactorEnroll
          enrolled={phoneEnrolled}
          onEnrolled={() => void query.refetch()}
        />
      </SectionCard>

      <SectionCard title={t('admin.me.sectionTwoFactor')}>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2">
              <StatusBadge
                label={
                  me.twoFactorVerified
                    ? t('admin.me.twoFactorSessionVerified')
                    : t('admin.me.twoFactorSessionUnverified')
                }
                variant={me.twoFactorVerified ? 'success' : 'neutral'}
                size="sm"
              />
            </span>
            <Button variant="outline" size="sm" onClick={() => setRegenerateOpen(true)}>
              {t('admin.me.twoFactorRegenerate')}
            </Button>
          </div>
          <p className="text-sm" style={{ color: 'var(--content-muted)' }}>
            {t('admin.me.twoFactorHint')}
          </p>
        </div>
      </SectionCard>

      <TwoFactorRegenerateDialog open={regenerateOpen} onOpenChange={setRegenerateOpen} />
    </PageShell>
  );
};
